import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/react-hooks';
import { useCallback, useEffect, useRef } from 'react';
import { fieldErrorsOf, GENERIC_ERROR } from 'shared/apolloFieldErrors';
import {
  ADD_FORM_NOTE,
  ARCHIVE_FORM,
  CREATE_FORM,
  CREATE_FORM_ASSET_UPLOAD,
  CREATE_SAVED_FORM_FIELD,
  DELETE_SAVED_FORM_FIELD,
  DUPLICATE_FORM,
  EXPORT_FORM_SUBMISSIONS,
  RESTORE_FORM,
  SAVE_FORM,
  SET_FORM_STATUS,
  SET_FORM_STATUS_CARD,
  UPDATE_FORM_SUBMISSION_ADMIN,
} from './mutations';
import {
  GET_EXPORT_RUN_DOWNLOAD_URL,
  GET_FORM_ADMIN,
  GET_FORM_FILE_DOWNLOAD_URL,
  GET_FORM_NOTES,
  GET_FORM_RECIPIENT_SUGGESTIONS,
  GET_FORM_SUBMISSION,
  GET_FORM_SUBMISSION_STATS,
  GET_FORM_SUBMISSIONS,
  GET_FORMS_ADMIN,
  GET_FORMS_SUMMARY,
  GET_SAVED_FORM_FIELDS,
} from './queries';
import { EXPORT_RUN_STATUS } from './subscriptions';

// Data hooks for Forms (build plan §5.3).
//
// Queries return the Apollo result plus the unwrapped data under a friendly
// name: { ...result, forms, count }.
//
// Mutation hooks (and the imperative ones: the file link and the export)
// return an async function that ALWAYS resolves to exactly one of
//   { data }   — the operation's root field (e.g. the BuilderForm)
//   { error }  — { global, fields, code } from fieldErrorsOf
// It never throws and never resolves to null, so callers write
//   const { data, error } = await createForm(input);
//   if (error) { setErrors(error.fields); showToast(error.global); return; }

// Every Forms operation shows its own not-found state (the builder's empty
// state, the entry drawer's notice, a toast), so a 404 must not send the
// whole app to ?404 (src/apollo.js, redirectsOn404).
export const FORMS_CONTEXT = { handles404: true };
export const withContext = (options = {}) => ({
  ...options,
  context: { ...options.context, ...FORMS_CONTEXT },
});

const EMPTY_ERROR = () => ({
  error: { global: GENERIC_ERROR, fields: {}, code: null },
});

// Shared with pages/app/W9/gql/hooks.js, so the W-9 hooks resolve to the
// same { data } | { error } shape.
export const settle = async (promise, root) => {
  try {
    const res = await promise;
    if (res && res.errors && res.errors.length) {
      return { error: fieldErrorsOf({ graphQLErrors: res.errors }) };
    }
    const data = res && res.data ? res.data[root] : undefined;
    if (data === undefined || data === null) return EMPTY_ERROR();
    return { data };
  } catch (e) {
    return { error: fieldErrorsOf(e) };
  }
};

const LIST_REFETCH = ['GetFormsAdmin', 'GetFormsSummary'];

// ---- Queries ---------------------------------------------------------------

export const useGetFormsAdmin = (input) => {
  const result = useQuery(
    GET_FORMS_ADMIN,
    withContext({
      variables: { input },
      fetchPolicy: 'cache-and-network',
    })
  );
  const payload = result.data && result.data.getFormsAdmin;
  return {
    ...result,
    forms: (payload && payload.results) || [],
    count: (payload && payload.count) || 0,
  };
};

export const useGetFormsSummary = (view) => {
  const result = useQuery(
    GET_FORMS_SUMMARY,
    withContext({
      variables: { input: view ? { view } : {} },
      fetchPolicy: 'cache-and-network',
    })
  );
  return {
    ...result,
    summary: (result.data && result.data.getFormsSummary) || null,
  };
};

export const useGetFormAdmin = (id) => {
  const result = useQuery(
    GET_FORM_ADMIN,
    withContext({
      variables: { id: Number(id) },
      skip: !id,
      fetchPolicy: 'network-only',
    })
  );
  return { ...result, form: (result.data && result.data.getFormAdmin) || null };
};

export const useGetFormNotes = (formId, { skip = false } = {}) => {
  const result = useQuery(
    GET_FORM_NOTES,
    withContext({
      variables: { form_id: Number(formId) },
      skip: skip || !formId,
      fetchPolicy: 'cache-and-network',
    })
  );
  return { ...result, notes: (result.data && result.data.getFormNotes) || [] };
};

export const useGetSavedFormFields = (input) => {
  const result = useQuery(
    GET_SAVED_FORM_FIELDS,
    withContext({
      variables: input ? { input } : {},
    })
  );
  return {
    ...result,
    savedFields: (result.data && result.data.getSavedFormFields) || [],
  };
};

// [search(account_id, text), { suggestions, loading, ... }]
export const useFormRecipientSuggestions = () => {
  const [run, result] = useLazyQuery(
    GET_FORM_RECIPIENT_SUGGESTIONS,
    withContext()
  );
  const search = useCallback(
    (accountId, text) =>
      run({
        variables: {
          input: { account_id: Number(accountId), search: text || '' },
        },
      }),
    [run]
  );
  return [
    search,
    {
      ...result,
      suggestions:
        (result.data && result.data.getFormRecipientSuggestions) || [],
      loading: !!result.loading,
    },
  ];
};

export const useGetFormSubmissions = (input, options = {}) => {
  const result = useQuery(
    GET_FORM_SUBMISSIONS,
    withContext({
      variables: { input },
      skip: !input || !input.form_id,
      ...options,
    })
  );
  const payload = result.data && result.data.getFormSubmissions;
  return {
    ...result,
    submissions: (payload && payload.results) || [],
    count: (payload && payload.count) || 0,
    columns: (payload && payload.columns) || [],
    versions: (payload && payload.versions) || [],
  };
};

export const useGetFormSubmission = (id, { skip = false } = {}) => {
  const result = useQuery(
    GET_FORM_SUBMISSION,
    withContext({
      variables: { id: Number(id) },
      skip: skip || !id,
    })
  );
  return {
    ...result,
    submission: (result.data && result.data.getFormSubmission) || null,
  };
};

export const useGetFormSubmissionStats = (formId, options = {}) => {
  const result = useQuery(
    GET_FORM_SUBMISSION_STATS,
    withContext({
      variables: { form_id: Number(formId) },
      skip: !formId,
      ...options,
    })
  );
  return {
    ...result,
    stats: (result.data && result.data.getFormSubmissionStats) || null,
  };
};

// (id) => Promise<{ url, filename, expires_at } | { error }>
export const useFormFileDownloadUrl = () => {
  const client = useApolloClient();
  return useCallback(
    async (id) => {
      const { data, error } = await settle(
        client.query(
          withContext({
            query: GET_FORM_FILE_DOWNLOAD_URL,
            variables: { id: Number(id) },
            fetchPolicy: 'network-only',
          })
        ),
        'getFormFileDownloadUrl'
      );
      return error ? { error } : { ...data };
    },
    [client]
  );
};

// ---- Mutations -------------------------------------------------------------

// (input: CreateFormInput) => { data: BuilderForm } | { error }
export const useCreateForm = () => {
  const [mutate] = useMutation(
    CREATE_FORM,
    withContext({ refetchQueries: LIST_REFETCH })
  );
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'createForm'),
    [mutate]
  );
};

// (input: SaveFormInput) — no refetch: the result updates the cache.
export const useSaveForm = () => {
  const [mutate] = useMutation(SAVE_FORM, withContext());
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'saveForm'),
    [mutate]
  );
};

// Builder: ({ id, status }) — no optimistic response.
export const useSetFormStatus = () => {
  const [mutate] = useMutation(SET_FORM_STATUS, withContext());
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'setFormStatus'),
    [mutate]
  );
};

// The card's own fields with the new status. __typename on every object so
// Apollo writes the optimistic result without missing-field warnings.
export const optimisticCard = (form, status) => ({
  __typename: 'Mutation',
  setFormStatus: {
    __typename: 'BuilderForm',
    id: form.id,
    public_id: form.public_id,
    name: form.name,
    status,
    archived_at: form.archived_at || null,
    revision: form.revision,
    account: {
      __typename: 'FormAccount',
      id: form.account.id,
      name: form.account.name,
    },
    field_count: form.field_count,
    submission_count: form.submission_count,
    note_count: form.note_count,
    created_at: form.created_at,
    updated_at: form.updated_at,
  },
});

// List card switch: (form, status). Optimistic; Apollo rolls it back when
// the server says no.
export const useSetFormStatusCard = () => {
  const [mutate] = useMutation(
    SET_FORM_STATUS_CARD,
    withContext({
      refetchQueries: ['GetFormsSummary'],
    })
  );
  return useCallback(
    (form, status) =>
      settle(
        mutate({
          variables: { input: { id: form.id, status } },
          optimisticResponse: optimisticCard(form, status),
        }),
        'setFormStatus'
      ),
    [mutate]
  );
};

// (id)
export const useArchiveForm = () => {
  const [mutate] = useMutation(
    ARCHIVE_FORM,
    withContext({ refetchQueries: LIST_REFETCH })
  );
  return useCallback(
    (id) => settle(mutate({ variables: { id: Number(id) } }), 'archiveForm'),
    [mutate]
  );
};

// (id)
export const useRestoreForm = () => {
  const [mutate] = useMutation(
    RESTORE_FORM,
    withContext({ refetchQueries: LIST_REFETCH })
  );
  return useCallback(
    (id) => settle(mutate({ variables: { id: Number(id) } }), 'restoreForm'),
    [mutate]
  );
};

// (input: { id, name?, account_id? })
export const useDuplicateForm = () => {
  const [mutate] = useMutation(
    DUPLICATE_FORM,
    withContext({
      refetchQueries: LIST_REFETCH,
    })
  );
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'duplicateForm'),
    [mutate]
  );
};

// (input: { form_id, body })
export const useAddFormNote = () => {
  const [mutate] = useMutation(
    ADD_FORM_NOTE,
    withContext({
      refetchQueries: ['GetFormNotes', 'GetFormsAdmin'],
    })
  );
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'addFormNote'),
    [mutate]
  );
};

// (input: { name, base_type, config, account_id? })
export const useCreateSavedFormField = () => {
  const [mutate] = useMutation(
    CREATE_SAVED_FORM_FIELD,
    withContext({
      refetchQueries: ['GetSavedFormFields'],
    })
  );
  return useCallback(
    (input) => settle(mutate({ variables: { input } }), 'createSavedFormField'),
    [mutate]
  );
};

// (id) => { data: true } | { error }
export const useDeleteSavedFormField = () => {
  const [mutate] = useMutation(
    DELETE_SAVED_FORM_FIELD,
    withContext({
      refetchQueries: ['GetSavedFormFields'],
    })
  );
  return useCallback(
    (id) =>
      settle(mutate({ variables: { id: Number(id) } }), 'deleteSavedFormField'),
    [mutate]
  );
};

// (input: { form_id, filename, content_type, size_bytes }) => { data: ticket }
export const useCreateFormAssetUpload = () => {
  const [mutate] = useMutation(CREATE_FORM_ASSET_UPLOAD, withContext());
  return useCallback(
    (input) =>
      settle(mutate({ variables: { input } }), 'createFormAssetUpload'),
    [mutate]
  );
};

// Staff edit of an entry's answers: ({ id, answers }) — `answers` holds
// only the changed fields. The returned row updates the cached entry and
// table row; stats don't change.
export const useUpdateFormSubmissionAdmin = () => {
  const [mutate] = useMutation(UPDATE_FORM_SUBMISSION_ADMIN, withContext());
  return useCallback(
    (input) =>
      settle(mutate({ variables: { input } }), 'updateFormSubmissionAdmin'),
    [mutate]
  );
};

// ---- Exports ---------------------------------------------------------------

export const EXPORT_FAILED = 'Export failed';
export const EXPORT_STOPPED = 'Export stopped';
export const EXPORT_TIMED_OUT =
  'The export is taking longer than expected. Please try again.';

// fieldErrorsOf's shape for a message of our own (cleaned, never raw SQL).
const errorFor = (message) => ({ error: fieldErrorsOf({ message }) });

// A subscription error is usually an ApolloError; subscriptions-transport-ws
// can also hand over the bare GraphQL errors array.
const subscriptionError = (e) => ({
  error: fieldErrorsOf(Array.isArray(e) ? { graphQLErrors: e } : e),
});

// What one exportRunStatus update means: { data: run } once the file is
// ready, { error } when the run failed, null while it is still pending or
// processing.
export const exportRunOutcome = (run) => {
  if (!run) return null;
  if (run.status === 'COMPLETED') return { data: run };
  if (run.status === 'FAILED') return errorFor(run.error || EXPORT_FAILED);
  return null;
};

// Follows run `id` until it finishes. Resolves { data: run } | { error }.
// `tracker` ({ alive, stops }) belongs to the page: once it unmounts,
// every run still followed resolves EXPORT_STOPPED and unsubscribes.
export const followExportRun = (client, id, tracker) => {
  if (!tracker.alive) return Promise.resolve(errorFor(EXPORT_STOPPED));
  return new Promise((resolve) => {
    let subscription = null;
    let settled = false;
    const finish = (outcome) => {
      if (settled) return;
      settled = true;
      tracker.stops.delete(stop);
      if (subscription) subscription.unsubscribe();
      resolve(outcome);
    };
    const stop = () => finish(errorFor(EXPORT_STOPPED));
    tracker.stops.add(stop);
    subscription = client
      .subscribe(
        withContext({ query: EXPORT_RUN_STATUS, variables: { id: Number(id) } })
      )
      .subscribe({
        next: ({ data }) => {
          const outcome = exportRunOutcome(data && data.exportRunStatus);
          if (outcome) finish(outcome);
        },
        error: (e) => finish(subscriptionError(e)),
        // The server gives up after 15 minutes without a finished run.
        complete: () => finish(errorFor(EXPORT_TIMED_OUT)),
      });
    // An update delivered during subscribe() finished before
    // `subscription` was set.
    if (settled) subscription.unsubscribe();
  });
};

// Export submissions on the worker: queue the file, follow the run until
// it is COMPLETED, then fetch its signed link.
// (input: ExportFormSubmissionsInput) =>
//   Promise<{ data: { run, url } } | { error }>
export const useFormSubmissionsExport = () => {
  const client = useApolloClient();
  const [mutate] = useMutation(EXPORT_FORM_SUBMISSIONS, withContext());
  const tracker = useRef({ alive: true, stops: new Set() });
  useEffect(() => {
    const runs = tracker.current;
    return () => {
      runs.alive = false;
      runs.stops.forEach((stop) => stop());
    };
  }, []);
  return useCallback(
    async (input) => {
      const started = await settle(
        mutate({ variables: { input } }),
        'exportFormSubmissions'
      );
      if (started.error) return started;
      const finished = await followExportRun(
        client,
        started.data.id,
        tracker.current
      );
      if (finished.error) return finished;
      const link = await settle(
        client.query(
          withContext({
            query: GET_EXPORT_RUN_DOWNLOAD_URL,
            variables: { id: Number(started.data.id) },
            fetchPolicy: 'network-only',
          })
        ),
        'getExportRunDownloadUrl'
      );
      if (link.error) return link;
      return { data: { run: finished.data, url: link.data } };
    },
    [client, mutate]
  );
};
